import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Button from "@/v2-backoffice-client/components/button"
import Link from "@/v2-backoffice-client/components/link"
import { FENERUM_ACCOUNT_BASE_URL } from "@/v2-backoffice-client/constants"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import LicenseCreateDialog from "@/v2-backoffice-client/license/create/LicenseCreateDialog"
import { useLicenseGoverningStatuses } from "@/v2-backoffice-client/license/license.statuses.hooks"
import { LICENSE_SOURCE_FENERUM } from "@/v2-backoffice-client/license/license.constants"
import {
  getCoverageIssueMeta,
  toneOfSeverity,
  type LicenseCoverageRow,
  type LicenseCoverageTone,
} from "@/v2-backoffice-client/license/coverage/license.coverage.utils"

export type LicenseCoverageIssueListProps = {
  coverage: LicenseCoverageRow
  /** Leave the "open customer" action out when the list already sits on the customer panel. */
  hideCustomerLink?: boolean
  className?: string
}

const TONE_CLASSES: Record<LicenseCoverageTone, string> = {
  critical: "border-red-500/50 bg-red-500/10",
  warning: "border-amber-500/50 bg-amber-500/10",
  info: "border-border bg-muted/40",
  none: "border-border",
}
const TONE_ICON_CLASSES: Record<LicenseCoverageTone, string> = {
  critical: "text-red-600 dark:text-red-400",
  warning: "text-amber-600 dark:text-amber-300",
  info: "text-muted-foreground",
  none: "text-muted-foreground",
}

// The issues on one license, each with the facts, the fix, and the levers:
// the bridge (an operator trial over the slot — the one override the
// hourly sync respects — through the grant dialog preset to the slot),
// Fenerum where the subscription itself is corrected, and the customer
// panel where a trial is moved, converted or ended. Automate by default,
// flag what the automation cannot know is wrong, make the correction one
// click.
function LicenseCoverageIssueList(props: LicenseCoverageIssueListProps) {
  const { coverage, hideCustomerLink, className } = props
  const { namesByCode } = useLicenseGoverningStatuses()

  if (coverage.Issues.length === 0) return null

  const canBridge =
    !coverage.IsLive && coverage.LicenseSourceCode === LICENSE_SOURCE_FENERUM
  const customerLink = coverage.ExternalCustomerId
    ? buildCustomerViewLink(coverage.ExternalCustomerId, "billing")
    : null

  return (
    <ul className={cn("flex flex-col gap-2", className)}>
      {coverage.Issues.map((issue) => {
        const meta = getCoverageIssueMeta(issue.Code)
        const tone = toneOfSeverity(issue.Severity)
        return (
          <li
            key={`${issue.Code}-${issue.FromDate ?? ""}`}
            className={cn(
              "flex flex-col gap-1.5 rounded-md border px-3 py-2 text-sm",
              TONE_CLASSES[tone],
            )}
            data-feature-code="UI_BackofficeLicenseCoverage_Issue"
          >
            <p className="flex items-start gap-2">
              <Icon
                src={meta.iconSrc}
                size={14}
                className={cn("mt-0.5 shrink-0", TONE_ICON_CLASSES[tone])}
                aria-hidden
              />
              <span>
                <span className="font-medium">{meta.label}.</span>{" "}
                <span>{issue.Message}</span>
                {issue.Hint && (
                  <span className="text-muted-foreground"> {issue.Hint}</span>
                )}
              </span>
            </p>
          </li>
        )
      })}

      <li className="flex flex-wrap items-center gap-2 pl-0.5">
        {canBridge && (
          <LicenseCoverageBridgeButton
            coverage={coverage}
            statusName={namesByCode.get(coverage.LicenseStatusCode)}
          />
        )}
        {coverage.BillingAccountUuid && (
          <Link
            variant="unstyled"
            iconSrc="bank"
            iconSize={12}
            external
            to={`${FENERUM_ACCOUNT_BASE_URL}/${coverage.BillingAccountUuid}/`}
            className="inline-flex items-center gap-1.5 rounded border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted"
            data-feature-code="UI_BackofficeLicenseCoverage_Fenerum"
          >
            Fix the subscription in Fenerum
          </Link>
        )}
        {!hideCustomerLink && customerLink && (
          <Link
            variant="unstyled"
            iconSrc="user"
            iconSize={12}
            to={customerLink}
            className="inline-flex items-center gap-1.5 rounded border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted"
            data-feature-code="UI_BackofficeLicenseCoverage_Customer"
          >
            Open the customer&apos;s licenses
          </Link>
        )}
      </li>
    </ul>
  )
}

type LicenseCoverageBridgeButtonProps = {
  coverage: LicenseCoverageRow
  statusName: string | undefined
}

// The manual override for a slot billing holds but has not made live: the
// grant dialog in bridge mode, preset to this license's product and billed
// plan, trial only (backoffice.Insert_Lic_License reclaims the row).
function LicenseCoverageBridgeButton(props: LicenseCoverageBridgeButtonProps) {
  const { coverage, statusName } = props
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <Button
        variant="primary"
        size="tiny"
        iconSrc="life-ring"
        iconSize={11}
        data-feature-code="UI_BackofficeLicenseCoverage_Bridge"
        onClick={() => {
          setIsOpen(true)
        }}
      >
        Bridge with a trial
      </Button>
      <LicenseCreateDialog
        customerId={coverage.CustomerId}
        customerName={coverage.CustomerName}
        bridge={{
          productCode: coverage.ProductCode,
          productName: coverage.ProductName,
          seatsAreAssigned: coverage.SeatsAreAssigned,
          planId: coverage.PlanId ?? null,
          statusName: statusName ?? "not live",
        }}
        open={isOpen}
        onOpenChange={setIsOpen}
      />
    </>
  )
}

export default LicenseCoverageIssueList
